import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToastContext, PageHeader } from "@resideo/blueprint-oneportal";
import { useAuth } from "@/contexts/AuthContext";
import {
  manageDevicesService,
  type Device,
} from "@/api/services/manageDevicesServices";
import { isTrumanCamera } from "@/utils/cameraFamily";
import CloseIcon from "@/assets/icons/close.svg?react";
import styles from "./ManageDevices.module.css";
import {
  ManageDevicesHeader,
  type DeviceTab,
} from "./components/ManageDevicesHeader/ManageDevicesHeader";
import { ManageDevicesSearch } from "./components/ManageDevicesSearch/ManageDevicesSearch";
import {
  ManageDevicesSelectionBar,
  type BulkAction,
} from "./components/ManageDevicesSelectionBar/ManageDevicesSelectionBar";
import {
  ManageDevicesTable,
  DEVICE_COLUMNS,
  type DeviceColumnKey,
} from "./components/ManageDevicesTable/ManageDevicesTable";
import {
  ManageDevicesFilterPanel,
  type DeviceFilterValues,
} from "./components/ManageDevicesFilterPanel/ManageDevicesFilterPanel";

const EMPTY_FILTERS: DeviceFilterValues = {
  statuses: [],
  deviceTypes: [],
  locations: [],
};

// All columns are visible by default.
const ALL_COLUMN_KEYS = DEVICE_COLUMNS.map((column) => column.key);

// Cap the number of in-flight enrichment requests per device group. Real users
// occasionally have dozens of devices, and firing every request in parallel
// wastes bandwidth (browsers throttle anyway) and can spike the backend.
const ENRICHMENT_CONCURRENCY = 6;

/**
 * Run an array of async factories with a fixed concurrency cap and return the
 * settled results in the same order as the input. A rejected task never
 * short-circuits the pool — this is effectively `Promise.allSettled` with a
 * worker pool in front of it.
 */
const runWithConcurrency = async <T,>(
  tasks: ReadonlyArray<() => Promise<T>>,
  limit: number
): Promise<PromiseSettledResult<T>[]> => {
  const results: PromiseSettledResult<T>[] = new Array(tasks.length);
  let cursor = 0;
  const workerCount = Math.min(Math.max(limit, 1), tasks.length);
  const workers = Array.from({ length: workerCount }, async () => {
    while (true) {
      const index = cursor++;
      if (index >= tasks.length) return;
      try {
        results[index] = { status: "fulfilled", value: await tasks[index]() };
      } catch (reason) {
        results[index] = { status: "rejected", reason };
      }
    }
  });
  await Promise.all(workers);
  return results;
};

// Translation keys for the raw `productFamily` values used as deviceType.
const DEVICE_TYPE_LABEL_KEYS: Record<string, string> = {
  Camera: "manageDevices.deviceTypes.camera",
  Thermostat: "manageDevices.deviceTypes.thermostat",
  SecurityPanel: "manageDevices.deviceTypes.securityPanel",
};

// Translation keys for the lowercased active-tab name used in empty messages.
const TAB_LOWER_LABEL_KEYS: Record<DeviceTab, string> = {
  "All Devices": "manageDevices.tabsLower.allDevices",
  Cameras: "manageDevices.tabsLower.cameras",
  Thermostats: "manageDevices.tabsLower.thermostats",
};

export const ManageDevices = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // `selectedContext` is null until the user picks one on the Context
  // Selection page; the fetch effect below no-ops in that state.
  const { selectedContext } = useAuth();

  // Resolve a raw device-type value to its localized display label.
  // Memoized to ensure it updates when language changes and is in dependencies correctly.
  const deviceTypeLabel = useCallback(
    (type: string) =>
      DEVICE_TYPE_LABEL_KEYS[type] ? t(DEVICE_TYPE_LABEL_KEYS[type]) : type,
    [t]
  );

  const [activeTab, setActiveTab] = useState<DeviceTab>("All Devices");
  const [searchTerm, setSearchTerm] = useState("");
  const { addToast } = useToastContext();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<DeviceFilterValues>(EMPTY_FILTERS);
  const [visibleColumns, setVisibleColumns] = useState<Set<DeviceColumnKey>>(
    () => new Set(ALL_COLUMN_KEYS)
  );
  const [columnOrder, setColumnOrder] = useState<DeviceColumnKey[]>(() => [
    ...ALL_COLUMN_KEYS,
  ]);

  const toggleColumn = (key: DeviceColumnKey) =>
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  // Present the columns in their current order so the menu and table stay in sync.
  const columnOptions = columnOrder.map((key) => ({
    key,
    label: DEVICE_COLUMNS.find((column) => column.key === key)?.label ?? key,
    visible: visibleColumns.has(key),
  }));

  const [devices, setDevices] = useState<Device[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const hasSelection = selectedIds.size > 0;
  const clearSelection = () => setSelectedIds(new Set());

  // Devices currently checked in the table.
  const selectedDevices = useMemo(
    () => devices.filter((d) => selectedIds.has(d.id)),
    [devices, selectedIds]
  );

  // "Set Schedules" only applies to thermostats, so it's offered only when
  // every selected device is a thermostat (hidden for mixed selections).
  const allSelectedAreThermostats =
    selectedDevices.length > 0 &&
    selectedDevices.every((d) => d.deviceType === "Thermostat");

  // Edit Controls only makes sense for non-camera devices — cameras have
  // their own Edit Settings flow, and the EditControls page is designed
  // around HVAC controls (System Mode, Fan Mode, setpoints).
  const allSelectedAreCameras =
    selectedDevices.length > 0 &&
    selectedDevices.every((d) => d.deviceType === "Camera");

  // The bulk schedule write cannot reach an offline device, so if any selected
  // thermostat is offline the action is shown but disabled with a tooltip.
  const hasOfflineSelectedThermostat = selectedDevices.some(
    (d) => d.deviceType === "Thermostat" && d.status === "offline",
  );

  // Check if all selected thermostats have the same CommercialConfiguration
  const hasDifferentCommercialConfiguration = useMemo(() => {
    if (!allSelectedAreThermostats || selectedDevices.length <= 1) return false;
    
    const firstConfig = JSON.stringify((selectedDevices[0].configuration as any)?.CommercialConfiguration);
    return selectedDevices.some(
      (d) => JSON.stringify((d.configuration as any)?.CommercialConfiguration) !== firstConfig
    );
  }, [allSelectedAreThermostats, selectedDevices]);

  // Edit Controls pushes state (system mode / fan mode / setpoints) to every
  // selected device, so a single offline row would silently drop its writes.
  const hasOfflineSelected = selectedDevices.some((d) => d.status === "offline");

  // Settings are only supported on Truman cameras today, so "Edit Settings"
  // is offered whenever at least one selected camera is Truman. Fenix/VX
  // cameras stay in the selection (so the page still reflects the full camera
  // count) but are flagged via nonTrumanCameraCount so the review page can
  // tell the user which settings won't apply to them.
  const selectedCameras = selectedDevices.filter((d) => d.deviceType === "Camera");
  const cameraIds = selectedCameras.map((d) => d.id);
  const nonTrumanCameraCount = selectedCameras.filter(
    (d) => !isTrumanCamera(d.globalDeviceType)
  ).length;
  const hasTrumanCamera = nonTrumanCameraCount < selectedCameras.length;

  // Determine if any selected camera is offline or in privacy mode. The
  // ManageDevices rows currently surface a `status` field (online/offline)
  // and may include richer states in the future (e.g. 'privacy-mode').
  // Treat either condition as a reason to hide the Edit Settings action
  // and surface a helpful hover warning instead.
  const hasOfflineOrPrivacySelectedCamera = selectedCameras.some(
    (d) => d.status === "offline" || (d.status === "privacy-mode")
  );

  // Bulk actions only make sense against a homogeneous selection — mixing
  // e.g. a camera and a thermostat has no shared destination page. When
  // the selection is mixed, the selection bar surfaces a message instead
  // of any action items.
  const isMixedTypeSelection =
    selectedDevices.length > 0 &&
    new Set(selectedDevices.map((d) => d.deviceType)).size > 1;
  const isMixedCameraFamilySelection =
    allSelectedAreCameras &&
    new Set(selectedCameras.map((d) => d.globalDeviceType ?? "")).size > 1;
  const isMixedBulkSelection = isMixedTypeSelection || isMixedCameraFamilySelection;

  const bulkActions = useMemo<BulkAction[]>(() => {
    if (isMixedBulkSelection) return [];

    // Resolve the device type used in the Edit Controls header. When the
    // selection is homogeneous we pass the actual type; for mixed selections
    // we fall back to a localized "Device" label so the page still reads
    // sensibly.
    const distinctTypes = new Set(selectedDevices.map((d) => d.deviceType));
    const editControlsDeviceType =
      distinctTypes.size === 1
        ? (distinctTypes.values().next().value as string)
        : t("manageDevices.editControlsMixedFallback");

    const goToEditControls = () => {
      navigate("/devices/edit-controls", {
        state: {
          deviceIds: selectedDevices.map((d) => d.id),
          deviceType: editControlsDeviceType,
          deviceCount: selectedDevices.length,
        },
      });
    };

    const actions: BulkAction[] = [];

    // Edit Controls targets HVAC controls (System Mode / Fan Mode /
    // setpoints), so it's suppressed when the selection is camera-only —
    // Edit Settings is the correct entry point for those.
    if (!allSelectedAreCameras) {
      actions.push({
        key: "edit-controls",
        label: t("manageDevices.bulkActions.editSettings"),
        disabled: hasOfflineSelected,
        disabledReason: hasOfflineSelected
          ? t("manageDevices.bulkActions.editControlsOfflineDisabled")
          : undefined,
        onSelect: goToEditControls,
      });
    }
    // Offer Edit Settings when there's at least one Truman camera selected.
    // If any selected camera is offline or in privacy mode, show the item
    // disabled with a tooltip explaining why it can't be used.
    if (hasTrumanCamera) {
      actions.push({
        key: "edit-settings",
        label: t("manageDevices.bulkActions.editControls"),
        disabled: hasOfflineOrPrivacySelectedCamera,
        disabledReason: hasOfflineOrPrivacySelectedCamera
          ? t("manageDevices.bulkActions.editSettingsOfflineDisabled")
          : undefined,
        onSelect: () => {
          // Edit Settings only applies to cameras — non-camera devices in a
          // mixed selection are intentionally excluded here, not silently dropped.
          const ids = cameraIds.join(',');
          navigate(`/camera/bulk-edit?cameras=${encodeURIComponent(ids)}`, {
            state: { nonTrumanCameraCount },
          });
        },
      });
    }
    if (allSelectedAreThermostats) {
      const isDisabled = hasOfflineSelectedThermostat || hasDifferentCommercialConfiguration;
      const disabledReason = hasOfflineSelectedThermostat
        ? t("manageDevices.bulkActions.setSchedulesOfflineDisabled")
        : hasDifferentCommercialConfiguration
        ? t("manageDevices.bulkActions.setSchedulesConfigMismatchDisabled")
        : undefined;

      actions.push({
        key: "set-schedules",
        label: t("manageDevices.bulkActions.setSchedules"),
        disabled: isDisabled,
        disabledReason: disabledReason,
        onSelect: () => {
          // deviceIds travel via both the query string (for deep-link support)
          // and location.state (fallback), matching the Edit Controls pattern.
          // Encode each id individually so the joining commas stay readable
          // in the URL bar instead of showing up as %2C.
          const ids = selectedDevices.map((d) => d.id);
          const encodedIds = ids.map(encodeURIComponent).join(",");
          navigate(`/devices/schedules?deviceIds=${encodedIds}`, {
            state: { deviceIds: ids, deviceCount: ids.length },
          });
        },
      });
    }
    // actions.push({
    //   key: "check-firmware",
    //   label: t("manageDevices.bulkActions.checkFirmware"),
    //   onSelect: () => {},
    // });
    return actions;
  }, [isMixedBulkSelection, hasTrumanCamera, cameraIds, nonTrumanCameraCount, allSelectedAreCameras, allSelectedAreThermostats, hasOfflineSelectedThermostat, hasDifferentCommercialConfiguration, hasOfflineSelected, t, selectedDevices, navigate]);

  // Uncomment together with the Add Device button in the title row below.
  // import { useNavigate } from "react-router-dom";
  // const { hasResourcePermission } = useAuth();
  // const canAddDevice = hasResourcePermission("DEVICE", "CREATE");
  // const handleAddDevice = () => navigate("/devices/add");

  // Fetch devices from the RIS Public API on mount.
  // Endpoint: GET /api/v2/accounts?accountType={selectedContext}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedContext === null) return;
    let cancelled = false;

    setIsLoading(true);
    setLoadError(null);

    manageDevicesService
      .fetchDevices(selectedContext ?? undefined)
      .then((rows) => {
        if (cancelled) return;
        setDevices(rows);

        // Progressive enhancement: fetch live status + firmware per device
        // group in parallel (bounded concurrency), then commit **one** state
        // update per group. This avoids the O(N²) `prev.map` fan-out and the
        // N re-renders that per-device `.then(setDevices)` produces.
        //
        // • Thermostats → GET /api/v2/devices/thermostats/{deviceId}
        //     status = Reported.Online, firmware = Reported.FirmwareSystem.SwPackage
        // • Cameras    → GET /api/v1/devices/cameras/{deviceId}/state
        //     status = online, firmware = version.softwareVersion
        //
        // Any per-device failure only falls back to "N/A" firmware for that
        // row (never blocks the group or the initial table render).
        const enrichGroup = async <T extends Record<string, any>>(
          ids: string[],
          fetcher: (id: string) => Promise<T>
        ) => {
          if (ids.length === 0) return;

          const settled = await runWithConcurrency(
            ids.map((id) => async () => ({ id, details: await fetcher(id) })),
            ENRICHMENT_CONCURRENCY
          );
          if (cancelled) return;

          const updates = new Map<string, T>();
          const failedIds = new Set<string>();
          for (let i = 0; i < settled.length; i++) {
            const result = settled[i];
            if (result.status === "fulfilled") {
              updates.set(result.value.id, result.value.details);
            } else {
              failedIds.add(ids[i]);
            }
          }

          // Single state commit per group. If neither map has this row, we
          // return the same reference so React skips re-rendering that row.
          setDevices((prev) =>
            prev.map((d) => {
              const update = updates.get(d.id);
              if (update) {
                return {
                  ...d,
                  ...update,
                };
              }
              if (failedIds.has(d.id) && !d.firmware) {
                return { ...d, firmware: "N/A" };
              }
              return d;
            })
          );
        };

        const thermostatIds = rows
          .filter((d) => d.deviceType === "Thermostat")
          .map((d) => d.id);
        const cameraIds = rows
          .filter((d) => d.deviceType === "Camera")
          .map((d) => d.id);

        // Fire both groups independently — each awaits internally, so the
        // camera group doesn't block behind thermostats (or vice versa).
        void enrichGroup(
          thermostatIds,
          async (id) => {
            const [details, configuration] = await Promise.all([
              manageDevicesService.fetchThermostatDetails(id),
              manageDevicesService.fetchThermostatConfiguration(id).catch(() => undefined)
            ]);
            return { ...details, configuration };
          }
        );
        void enrichGroup(
          cameraIds,
          manageDevicesService.fetchCameraDetails.bind(manageDevicesService)
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof Error
            ? err.message
            : t("manageDevices.loadFailedFallback");
        setLoadError(message);
        setDevices([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedContext]);

  // Map each tab to the API's `productFamily` value (== our `deviceType`).
  // "All Devices" is null so the filter is skipped entirely.
  const TAB_DEVICE_TYPES: Record<DeviceTab, string | null> = {
    "All Devices": null,
    Cameras: "Camera",
    Thermostats: "Thermostat",
  };

  // Distinct location names present in the fetched devices, used to populate
  // the Location filter options. Excludes empty/placeholder values.
  const availableLocations = useMemo(() => {
    const set = new Set(
      devices
        .map((d) => d.location)
        .filter((name) => name && name !== "\u2014")
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [devices]);

  // Distinct device types present in the fetched devices, mapped to display
  // labels for the filter panel.
  const deviceTypeOptions = useMemo(() => {
    const set = new Set(
      devices
        .map((d) => d.deviceType)
        .filter((type) => type && type !== "\u2014")
    );
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((type) => ({ label: deviceTypeLabel(type), value: type }));
  }, [devices, deviceTypeLabel]);

  // Drive tab visibility off the device types the account actually has, so the
  // Cameras / Thermostats tabs only appear when such devices exist.
  const hasCameras = useMemo(
    () => devices.some((d) => d.deviceType === "Camera"),
    [devices]
  );
  const hasThermostats = useMemo(
    () => devices.some((d) => d.deviceType === "Thermostat"),
    [devices]
  );

  useEffect(() => {
    if (
      (activeTab === "Cameras" && !hasCameras) ||
      (activeTab === "Thermostats" && !hasThermostats)
    ) {
      setActiveTab("All Devices");
    }
  }, [activeTab, hasCameras, hasThermostats]);

  const activeFilterCount =
    filters.statuses.length +
    filters.deviceTypes.length +
    filters.locations.length;

  // Flatten the active filter selections into removable chips shown under the
  // search bar. Device types use their friendly display label.
  const activeFilterChips = useMemo(() => {
    const chips: {
      key: string;
      group: keyof DeviceFilterValues;
      value: string;
      label: string;
      ariaLabel: string;
    }[] = [];

    filters.locations.forEach((value) =>
      chips.push({
        key: `location-${value}`,
        group: "locations",
        value,
        label: value,
        ariaLabel: t("manageDevices.chipLocation", { value }),
      })
    );
    filters.statuses.forEach((value) => {
      const label = t(`manageDevices.statusOptions.${value}`);
      chips.push({
        key: `status-${value}`,
        group: "statuses",
        value,
        label,
        ariaLabel: t("manageDevices.chipStatus", { value: label }),
      });
    });
    filters.deviceTypes.forEach((value) => {
      const label = deviceTypeLabel(value);
      chips.push({
        key: `type-${value}`,
        group: "deviceTypes",
        value,
        label,
        ariaLabel: t("manageDevices.chipType", { value: label }),
      });
    });

    return chips;
  }, [filters, t, deviceTypeLabel]);

  const removeFilter = (group: keyof DeviceFilterValues, value: string) =>
    setFilters((prev) => ({
      ...prev,
      [group]: prev[group].filter((v) => v !== value),
    }));

  const clearAllFilters = () => setFilters(EMPTY_FILTERS);

  // Filter devices by the active tab, the search term, AND the Filter panel
  // selections. Search matches (case-insensitive substring) against name,
  // location, and deviceType. Rows are sorted ascending by location
  // (case-insensitive, locale-aware) so the table has a stable, predictable
  // order regardless of the API response order.
  const filteredDevices = useMemo(() => {
    const tabType = TAB_DEVICE_TYPES[activeTab];
    const query = searchTerm.trim().toLowerCase();

    const rows = devices.filter((d) => {
      if (tabType && d.deviceType !== tabType) return false;
      if (filters.statuses.length && !filters.statuses.includes(d.status))
        return false;
      if (
        filters.deviceTypes.length &&
        !filters.deviceTypes.includes(d.deviceType)
      )
        return false;
      if (filters.locations.length && !filters.locations.includes(d.location))
        return false;
      if (!query) return true;
      return (
        d.name.toLowerCase().includes(query) ||
        d.location.toLowerCase().includes(query) ||
        d.deviceType.toLowerCase().includes(query)
      );
    });

    return [...rows].sort((a, b) =>
      (a.location ?? "").localeCompare(b.location ?? "", undefined, {
        sensitivity: "base",
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, activeTab, searchTerm, filters]);

  const tableEmptyMessage = isLoading
    ? t("manageDevices.loadingDevices")
    : loadError
      ? t("manageDevices.loadError", { error: loadError })
      : devices.length > 0 && searchTerm.trim().length > 0
        ? t("manageDevices.noResults", { term: searchTerm.trim() })
        : devices.length > 0 && activeTab !== "All Devices"
          ? t("manageDevices.noTypeFound", {
              type: t(TAB_LOWER_LABEL_KEYS[activeTab]),
            })
          : t("manageDevices.noData");

  // Surface success messages routed here via router state (e.g. Edit
  // Controls Apply on the shell, device-add flows) as a Blueprint toast.
  useEffect(() => {
    if (location.state?.message) {
      addToast({
        message: location.state.message,
        toastType: "Success",
      });

      // Clear the router state so a remount (e.g. tab focus, StrictMode
      // dev double-invoke) doesn't re-fire the toast. Using navigate keeps
      // React Router as the source of truth — `history.replaceState`
      // wouldn't notify the router.
      navigate(location.pathname + location.search, {
        replace: true,
        state: {},
      });
    }
  }, [location, addToast, navigate]);

  return (
    <div className={styles.container}>

      <PageHeader title={t("manageDevices.title")} />

      <div className={styles.contentCard}>
        <ManageDevicesHeader
          activeTab={activeTab}
          onTabChange={setActiveTab}
          hasCameras={hasCameras}
          hasThermostats={hasThermostats}
        />

        {hasSelection ? (
          <ManageDevicesSelectionBar
            selectedCount={selectedIds.size}
            onClear={clearSelection}
            actions={bulkActions}
            emptyMessage={
              isMixedBulkSelection
                ? t("manageDevices.mixedSelectionMessage")
                : undefined
            }
            hoverWarning={
              hasOfflineOrPrivacySelectedCamera
                ? t("manageDevices.selectionCameraPrivacyHoverWarning")
                : // Only when the camera-only selection has no CX6 at all — a
                  // CX4/CX6 mix still offers Edit Settings, so no caveat there.
                  allSelectedAreCameras && !hasTrumanCamera
                ? t("manageDevices.selectionUnsupportedCameraHoverWarning")
                : hasOfflineSelected
                ? t("manageDevices.selectionOfflineHoverWarning")
                : undefined
            }
          />
        ) : (
          <ManageDevicesSearch
            value={searchTerm}
            onChange={setSearchTerm}
            filterCount={activeFilterCount}
            onFiltersClick={() => setIsFilterOpen(true)}
            columns={columnOptions}
            onColumnToggle={toggleColumn}
            onColumnReorder={setColumnOrder}
          />
        )}

        {!hasSelection && activeFilterChips.length > 0 && (
          <div className={styles.activeFilters}>
            {activeFilterChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                className={styles.filterChip}
                onClick={() => removeFilter(chip.group, chip.value)}
                aria-label={t("manageDevices.removeFilter", {
                  label: chip.ariaLabel,
                })}
              >
                <span>{chip.label}</span>
                <CloseIcon
                  className={styles.filterChipRemove}
                  aria-hidden="true"
                />
              </button>
            ))}
            <button
              type="button"
              className={styles.clearAllFilters}
              onClick={clearAllFilters}
            >
              {t("manageDevices.clearAll")}
            </button>
          </div>
        )}

        <ManageDevicesTable
          devices={filteredDevices}
          emptyMessage={tableEmptyMessage}
          isLoading={isLoading}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          visibleColumns={visibleColumns}
          columnOrder={columnOrder}
        />
      </div>

      <ManageDevicesFilterPanel
        open={isFilterOpen}
        value={filters}
        deviceTypeOptions={deviceTypeOptions}
        locationOptions={availableLocations}
        isOptionsLoading={isLoading}
        onApply={(next) => {
          setFilters(next);
          setIsFilterOpen(false);
        }}
        onClose={() => setIsFilterOpen(false)}
      />
    </div>
  );
};

export default ManageDevices;
